import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { layersAPI } from '../../services/api';
import { Layer } from '../../types';

export const ManageLayers: React.FC = () => {
  const { logout } = useAuth();
  const queryClient = useQueryClient();
  const [editingLayer, setEditingLayer] = useState<Layer | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    color: '#10b981',
    isActive: true
  });

  // Fetch layers
  const { data: layersData, isLoading } = useQuery({
    queryKey: ['layers'],
    queryFn: () => layersAPI.getAll(),
  });

  const layers = layersData?.data || [];

  // Create layer mutation
  const createMutation = useMutation({
    mutationFn: (data: any) => layersAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['layers'] });
      setShowAddForm(false);
      resetForm();
      alert('Layer created successfully!');
    },
    onError: (error: any) => {
      alert('Error creating layer: ' + (error.response?.data?.message || error.message));
    },
  });

  // Update layer mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => layersAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['layers'] });
      setEditingLayer(null);
      resetForm();
      alert('Layer updated successfully!');
    },
    onError: (error: any) => {
      alert('Error updating layer: ' + (error.response?.data?.message || error.message));
    },
  });

  // Delete layer mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => layersAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['layers'] });
      alert('Layer deleted successfully!');
    },
    onError: (error: any) => {
      alert('Error deleting layer: ' + (error.response?.data?.message || error.message));
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      description: '',
      color: '#10b981',
      isActive: true
    });
  };

  const handleEdit = (layer: Layer) => {
    setEditingLayer(layer);
    setFormData({
      name: layer.name,
      code: layer.code,
      description: layer.description || '',
      color: layer.color,
      isActive: layer.isActive
    });
    setShowAddForm(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingLayer) {
      updateMutation.mutate({ id: editingLayer._id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDelete = (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to delete the layer "${name}"? This will also affect all POIs in this district.`)) {
      deleteMutation.mutate(id);
    }
  };

  if (isLoading) return <div style={{ padding: '20px' }}>Loading layers...</div>;

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '2px solid #e5e7eb', paddingBottom: '10px' }}>
        <h1>Manage District Layers</h1>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button
            onClick={() => { setShowAddForm(!showAddForm); setEditingLayer(null); resetForm(); }}
            style={{ background: '#10b981', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer' }}
          >
            {showAddForm ? 'Cancel' : '+ Add District'}
          </button>
          <Link to="/admin" style={{ color: '#3b82f6', textDecoration: 'none' }}>← Dashboard</Link>
          <button onClick={logout} style={{ background: '#ef4444', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer' }}>Logout</button>
        </div>
      </header>

      {/* Add/Edit Form */}
      {showAddForm && (
        <div style={{ background: '#f9fafb', padding: '20px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #e5e7eb' }}>
          <h3>{editingLayer ? 'Edit District Layer' : 'Add New District Layer'}</h3>
          <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '15px', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
                style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                placeholder="e.g., Sukoharjo"
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Code *</label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
                required
                style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
                placeholder="e.g., 331104"
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Color</label>
              <input
                type="color"
                value={formData.color}
                onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                style={{ width: '100%', padding: '4px', border: '1px solid #d1d5db', borderRadius: '4px', height: '40px' }}
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px', resize: 'vertical' }}
                placeholder="Description of the district..."
              />
            </div>
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                />
                Active
              </label>
            </div>
            <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '10px' }}>
              <button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
                style={{ background: '#3b82f6', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '6px', cursor: 'pointer' }}
              >
                {createMutation.isPending || updateMutation.isPending ? 'Saving...' : (editingLayer ? 'Update' : 'Create')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Layers List */}
      <div style={{ background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <div style={{ background: '#f9fafb', padding: '15px', borderBottom: '1px solid #e5e7eb' }}>
          <h3 style={{ margin: 0 }}>District Layers ({layers.length})</h3>
        </div>
        
        {layers.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
            <p>No district layers found. Click "Add District" to create your first layer.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '1px', background: '#e5e7eb' }}>
            {layers.map((layer: Layer) => (
              <div key={layer._id} style={{ background: 'white', padding: '15px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flex: 1 }}>
                  <div
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '4px',
                      backgroundColor: layer.color,
                      flexShrink: 0
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <h4 style={{ margin: '0 0 4px 0', color: '#111827' }}>
                      {layer.name} <span style={{ fontSize: '14px', color: '#6b7280' }}>({layer.code})</span>
                    </h4>
                    {layer.description && (
                      <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>{layer.description}</p>
                    )}
                    <div style={{ marginTop: '4px', fontSize: '12px', color: '#9ca3af' }}>
                      {layer.isActive ? '✅ Active' : '❌ Inactive'}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => handleEdit(layer)}
                    style={{ background: '#f59e0b', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(layer._id, layer.name)}
                    disabled={deleteMutation.isPending}
                    style={{ background: '#ef4444', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
